/**
 * Ciste selektory nad R3CaseFixture (BLOK 3): presunuto z
 * apps/web-admin/src/lib/case-data.ts, aby existovaly jednou — detail
 * zakazky (web-admin) i generatory reportu (tento balicek) je pouzivaji
 * stejne. Zadne DB volani, zadny fetch — cisty vypocet nad daty.
 *
 * Pravidla dat: fakt bez evidence se NEVYKRESLI; chybejici pole = null a
 * volajici ho ukaze jako „nezjištěno" — nic se nedomysli.
 */
/*
 * Krok PRF5 bod 2: stupnice rizika zaplavoveho uzemi je jedna — v kontextu.
 * Bere se PODCESTOU (`/flood-risk`), ne z barrelu: barrel by do balicku
 * administrace pritahl `@reos/db` -> `@reos/observability` ->
 * `node:async_hooks` a web-admin by se prestal sestavovat (tyz duvod jako
 * u `technical-report-html.ts`, ktery smi brat jen typy).
 */
import { floodZoneRiskLevel, inFloodZone } from '@reos/duediligence/flood-risk';
import { factRef } from './captions.js';
import { conflictGroups, displayFacts, hiddenFactCount } from './claims.js';
/** Cisty mapper (oddeleny kvuli unit testum bez fetch). */
export function toCaseDetail(fixture) {
    return {
        fixture,
        facts: displayFacts(fixture.facts),
        // Skryta jsou jen fakta bez evidence, ktera zaroven nicemu neodporuji —
        // rozporny vstup bez evidence se zobrazi jako neovereny (viz claims.ts).
        hiddenFactCount: hiddenFactCount(fixture.facts),
        conflictGroups: conflictGroups(fixture.facts),
        evidenceById: new Map(fixture.evidence.map((e) => [e.id, e])),
    };
}
// ---- selektory (cista data pro karty, zalozky i reporty) --------------------
function factsOf(detail, domain) {
    return detail.facts.filter((f) => f.domain === domain);
}
function factByKey(detail, key) {
    return detail.facts.find((f) => f.factKey === key) ?? null;
}
function recordValue(fact) {
    if (fact === null || fact.value === null || typeof fact.value !== 'object' || Array.isArray(fact.value)) {
        return null;
    }
    return fact.value;
}
function num(record, key) {
    const v = record?.[key];
    return typeof v === 'number' ? v : null;
}
function str(record, key) {
    const v = record?.[key];
    return typeof v === 'string' ? v : null;
}
/** Popisek puvodu udaje do dlazdice/podtitulku — nikdy technicky klic faktu. */
export const HEADER_ORIGIN_CAPTIONS = {
    scope: 'dle potvrzeného rozsahu zakázky',
    kn: 'dle evidence KN/RÚIAN',
    plan: 'dle uzamčeného situačního plánku',
};
/** Parcely vyjmenovane primo fakty katastru (`parcel.label#…`, `parcel.identity#…`, `parcel.area.kn_m2#…`). */
function parcelLabelsFromFacts(detail) {
    const labels = [];
    for (const fact of detail.facts) {
        if (!fact.factKey.startsWith('parcel.label#') &&
            !fact.factKey.startsWith('parcel.identity#') &&
            !fact.factKey.startsWith('parcel.area.kn_m2#')) {
            continue;
        }
        const label = str(recordValue(fact), 'label') ?? factRef(fact.factKey);
        if (label !== null && !labels.includes(label))
            labels.push(label);
    }
    return labels;
}
/** Parcely z grafu pripadu (uzly `PARCEL` — tytez, ze kterych se kresli plan). */
function parcelNodes(detail) {
    return detail.fixture.nodes.filter((n) => n.nodeType === 'PARCEL');
}
function externalNumber(ref, keys) {
    for (const key of keys) {
        const value = ref[key];
        if (typeof value === 'number')
            return value;
    }
    return null;
}
/**
 * Fakty hlavicky z tychz zdroju, ze kterych se kresli situacni plan (krok O7a,
 * vada 1). Poradi je od nejzavaznejsiho zdroje k nejslabsimu a NIKDY se
 * nemicha: kdyz existuje potvrzeny `scope.parcels`, plati on; jinak evidence
 * katastru (fakty a uzly grafu); jako posledni to, co uz je nakreslene v
 * uzamcenem planku. Kdyz nema zadny zdroj, zustava prazdno — „nezjištěno" se
 * tak objevi jen tam, kde udaj opravdu chybi.
 */
export function caseHeaderFacts(detail) {
    const scope = recordValue(factByKey(detail, 'scope.parcels'));
    const scopeParcels = scope?.['parcels'];
    const scopeLabels = Array.isArray(scopeParcels)
        ? scopeParcels.filter((p) => typeof p === 'string')
        : [];
    const nodes = parcelNodes(detail);
    const nodeLabels = nodes
        .map((n) => n.nodeLabel)
        .filter((label) => typeof label === 'string' && label.length > 0);
    const factLabels = parcelLabelsFromFacts(detail);
    const planLabels = (detail.fixture.sitePlan?.geometry.parcels ?? []).map((p) => p.ref);
    const parcels = [
        [scopeLabels, 'scope'],
        [factLabels, 'kn'],
        [nodeLabels, 'kn'],
        [planLabels, 'plan'],
    ];
    const [parcelLabels, parcelOrigin] = parcels.find(([labels]) => labels.length > 0) ?? [[], null];
    // Nazev k.u. je v `scope.parcels` (land-01) nebo v `externalRef` uzlu parcely
    // (Petrovice/Lysa: { ku, kod } resp. { ku, ku_code }); kod umi doplnit i
    // `parcel.identity#…` (`cadastralAreaCode`), i kdyz nazev nikde neni.
    const nodeRef = nodes.map((n) => n.externalRef).find((ref) => typeof ref['ku'] === 'string') ?? null;
    const identityCode = detail.facts
        .filter((f) => f.factKey.startsWith('parcel.identity#'))
        .map((f) => num(recordValue(f), 'cadastralAreaCode'))
        .find((code) => code !== null);
    const scopeName = str(scope, 'cadastralAreaName');
    const nodeName = nodeRef !== null ? nodeRef['ku'] : null;
    const cadastralAreaName = scopeName ?? nodeName;
    const cadastralAreaCode = (scopeName !== null ? num(scope, 'cadastralArea') : null) ??
        (nodeRef !== null ? externalNumber(nodeRef, ['kod', 'ku_code', 'cadastralAreaCode']) : null) ??
        identityCode ??
        null;
    return {
        parcelLabels,
        parcelOrigin,
        cadastralAreaName,
        cadastralAreaCode,
        cadastralAreaOrigin: cadastralAreaName === null ? null : scopeName !== null ? 'scope' : 'kn',
    };
}
export function caseHeader(detail) {
    const { caseInfo } = detail.fixture;
    const [head, ...rest] = caseInfo.addressQuery.split(',');
    const facts = caseHeaderFacts(detail);
    return {
        address: (head ?? caseInfo.addressQuery).trim(),
        location: rest.length > 0 ? rest.join(',').trim() : null,
        kind: caseInfo.kind,
        status: caseInfo.status,
        openedAt: caseInfo.openedAt,
        parcelCount: facts.parcelLabels.length > 0 ? facts.parcelLabels.length : null,
        // Dokud neni potvrzeny rozsah, hlavicka NESMI ukazovat zadne cislo jako
        // vymeru predmetu prodeje (Milnik 1 bod 7) — resolver vraci null.
        totalAreaM2: areaReconciliationResult({ ...detail, facts: detail.facts }).confirmedSubjectTotalM2,
        cadastralAreaName: facts.cadastralAreaName,
        parcelOrigin: facts.parcelOrigin,
        cadastralAreaOrigin: facts.cadastralAreaOrigin,
        cadastralAreaCode: facts.cadastralAreaCode,
    };
}
const BAND_RANK = {
    VERY_HIGH: 5,
    HIGH: 4,
    MEDIUM: 3,
    LOW: 2,
    VERY_LOW: 1,
};
export function areaRows(detail) {
    const byLabel = new Map();
    for (const fact of detail.facts) {
        if (!fact.factKey.startsWith('parcel.area.kn_m2#'))
            continue;
        const record = recordValue(fact);
        const label = str(record, 'label');
        const area = num(record, 'areaM2');
        if (label === null || area === null)
            continue;
        const existing = byLabel.get(label);
        const rank = BAND_RANK[fact.trustBand ?? ''] ?? 0;
        const existingRank = existing !== undefined ? (BAND_RANK[existing.trustBand ?? ''] ?? 0) : -1;
        if (rank > existingRank) {
            byLabel.set(label, { parcelLabel: label, areaM2: area, trustBand: fact.trustBand, factId: fact.id });
        }
    }
    return [...byLabel.values()];
}
/** Brana rozsahu predmetu prodeje. Dokud neni CLOSED, potvrzena vymera neexistuje. */
function scopeGateStatus(detail) {
    const gate = detail.fixture.gates.find((g) => g.gateId === 'GATE_SCOPE');
    if (gate === undefined)
        return 'UNKNOWN';
    return gate.state === 'CLOSED' ? 'CONFIRMED' : 'OPEN';
}
/**
 * Jediny sdileny resolver vymer pro UI i PDF (Milnik 1, bod 7).
 *
 * Cte tri tvary, ktere realne v datech jsou — zadny z nich se nehada:
 *  1) `geometry.plan_vs_kn_match` — { total: { kn_m2, plan_m2 }, verdict } (LAND-01),
 *  2) `scope.total_area_m2` — { knVerifiedSumM2, brokerClaimedM2, deltaM2 } (Petrovice),
 *  3) skupina `parcel.area.subject_total_m2` — vice faktu s `areaM2`, kde rucni
 *     podklad je ten se statusem CONFLICT/USER_INPUT (Lysa).
 * Kdyz zadny tvar nesedi, vraci se UNKNOWN — nic se nedopocitava.
 */
export function areaReconciliationResult(detail) {
    const scopeStatus = scopeGateStatus(detail);
    const empty = {
        knCandidateTotalM2: null,
        manualInputTotalM2: null,
        confirmedSubjectTotalM2: null,
        scopeStatus,
        reconciliationStatus: 'UNKNOWN',
        differenceM2: null,
        factIds: [],
    };
    const finish = (kn, manual, factIds) => {
        const difference = kn !== null && manual !== null ? Math.abs(kn - manual) : null;
        const reconciliationStatus = kn === null || manual === null ? 'UNKNOWN' : difference === 0 ? 'MATCH' : 'CONFLICT';
        // Potvrzena vymera existuje jen pri zavrene brane rozsahu A bez rozporu.
        const confirmed = scopeStatus === 'CONFIRMED' && reconciliationStatus !== 'CONFLICT' && kn !== null ? kn : null;
        return {
            knCandidateTotalM2: kn,
            manualInputTotalM2: manual,
            confirmedSubjectTotalM2: confirmed,
            scopeStatus,
            reconciliationStatus,
            differenceM2: difference,
            factIds,
        };
    };
    // 1) plan_vs_kn_match
    const match = factByKey(detail, 'geometry.plan_vs_kn_match');
    const matchRecord = recordValue(match);
    if (matchRecord !== null) {
        const total = matchRecord['total'];
        const totalRecord = typeof total === 'object' && total !== null && !Array.isArray(total)
            ? total
            : null;
        const kn = num(totalRecord, 'kn_m2');
        const plan = num(totalRecord, 'plan_m2');
        if (kn !== null || plan !== null)
            return finish(kn, plan, match === null ? [] : [match.id]);
    }
    // 2) scope.total_area_m2 s obema hodnotami v jedne value
    const scopeFact = factByKey(detail, 'scope.total_area_m2');
    const scopeRecord = recordValue(scopeFact);
    if (scopeRecord !== null) {
        const kn = num(scopeRecord, 'knVerifiedSumM2');
        const manual = num(scopeRecord, 'brokerClaimedM2');
        if (kn !== null || manual !== null)
            return finish(kn, manual, scopeFact === null ? [] : [scopeFact.id]);
    }
    // 3) skupina parcel.area.subject_total_m2 — dve hodnoty ve dvou faktech
    const totals = detail.facts.filter((f) => f.factKey === 'parcel.area.subject_total_m2');
    if (totals.length > 0) {
        const manualFact = totals.find((f) => f.status === 'CONFLICT' || f.status === 'USER_INPUT') ?? null;
        const knFact = totals.find((f) => f.id !== manualFact?.id) ?? null;
        const kn = num(recordValue(knFact), 'areaM2');
        const manual = num(recordValue(manualFact), 'areaM2');
        const ids = [knFact?.id, manualFact?.id].filter((id) => id !== undefined);
        if (kn !== null || manual !== null)
            return finish(kn, manual, ids);
    }
    return empty;
}
/** @deprecated Skalarni tvar; pouzij `areaReconciliationResult`. */
export function areaReconciliation(detail) {
    const fact = factByKey(detail, 'geometry.plan_vs_kn_match');
    const record = recordValue(fact);
    const total = record?.['total'];
    const totalRecord = total !== null && typeof total === 'object' && !Array.isArray(total)
        ? total
        : null;
    return {
        verdict: str(record, 'verdict'),
        totalKnM2: num(totalRecord, 'kn_m2'),
        totalPlanM2: num(totalRecord, 'plan_m2'),
        factId: fact?.id ?? null,
    };
}
export function historyEvents(detail) {
    const { caseInfo, research, generated } = detail.fixture;
    const events = [
        { id: 'opened', caption: 'Založení zakázky', at: caseInfo.openedAt },
    ];
    // Krok UXP3 (P1-5): popisky mluvi o tom, co se stalo se ZAKAZKOU, ne
    // o vnitrnim behu („Start autonomního auditu", „Export snímku dat" — rucni
    // pruchod 10. 10. 2026 je nasel v historii zakazky, kam se divá makler).
    if (research.startedAt !== null) {
        events.push({ id: 'run-start', caption: 'Začalo prověřování', at: research.startedAt });
    }
    if (research.finishedAt !== null) {
        events.push({ id: 'run-finish', caption: 'Prověřování dokončeno', at: research.finishedAt });
    }
    events.push({ id: 'export', caption: 'Uložení podkladů prověření', at: generated.exportedAt });
    return events.reverse();
}
/**
 * Krok PIL3 bod 1 (nalez F01) — JEDINE MISTO, KTERE CTE STAV BRANY.
 *
 * Volaji ho UI detailu zakazky, interni audit i prezentace majiteli. Brana,
 * kterou zakazka v datech NEMA, neni dukaz niceho: vraci `UNKNOWN`, a volajici
 * z nej napise „nezjištěno". Driv tu stal boolean a chybejici brana se cetla
 * jako kladny zaver.
 */
export function gateVerdict(detail, gateId) {
    const gate = detail.fixture.gates.find((g) => g.gateId === gateId);
    if (gate === undefined)
        return 'UNKNOWN';
    return gate.state === 'CLOSED' ? 'DOCUMENTED' : 'OPEN';
}
export function accessSummary(detail) {
    const frontage = recordValue(factByKey(detail, 'access.street_frontage'));
    // Krok G2 zapisuje JEDEN fakt `access.corridor_max_width` PER SCENAR (AS_IS
    // i kazdy AFTER_REMOVAL:<ref> sdileji stejny factKey) s POLEM `maxWidthM`
    // (camelCase) — starsi selektor cetl `max_width_m` (snake_case) a nikdy nic
    // nenasel (nalez G3, proto PDF Podebradovy drzelo predG2 hodnotu 7,3 m).
    // Souhrnna dlazdice vzdy ukazuje scenar AS_IS (stav ted), nikdy hypotezu
    // "po odstraneni" jako by uz platila.
    const corridorFacts = detail.facts.filter((f) => f.factKey === 'access.corridor_max_width');
    const corridor = recordValue(corridorFacts.find((f) => recordValue(f)?.['scenario'] === 'AS_IS') ?? corridorFacts[0] ?? null);
    const stats = detail.fixture.sitePlan?.corridorStats ?? null;
    const landlocked = factByKey(detail, 'access.rear_parcels_landlocked');
    return {
        frontageM: num(frontage, 'frontage_m'),
        corridorMaxWidthM: num(corridor, 'maxWidthM'),
        corridorWidthM: stats?.widthM ?? null,
        corridorPassable: stats?.passable ?? null,
        rearParcelsLandlocked: landlocked !== null,
        legalAccess: gateVerdict(detail, 'GATE_LEGAL_ACCESS'),
    };
}
export function utilitiesSummary(detail) {
    const connections = recordValue(factByKey(detail, 'utilities.house_connections_ruian'));
    const operatorsRecord = recordValue(factByKey(detail, 'utilities.operators'));
    const operators = operatorsRecord !== null
        ? Object.fromEntries(Object.entries(operatorsRecord).filter((e) => typeof e[1] === 'string'))
        : null;
    return {
        water: str(connections, 'vodovod'),
        sewer: str(connections, 'kanalizace'),
        gas: str(connections, 'plyn'),
        operators,
        sewerCapacity: gateVerdict(detail, 'GATE_SEWER_CAPACITY'),
    };
}
/**
 * Krok PRF5 bod 2 — ZASAHUJE VRSTVA ZAPLAVOVEHO UZEMI PREDMET?
 *
 * Odpoved lezi ve faktech pod vic klici a KAZDE PARCELY zvlast
 * (`environment.flood.q100#639`), navic ve dvou pravopisech (`present`,
 * `inFloodZone`) — pravopisy zna jedna funkce kontextu (`inFloodZone`),
 * tady se jen hleda spravny radek. `null` = odpoved jeste nemame.
 */
function floodLayerReaches(detail, suffix) {
    const rows = detail.facts.filter((row) => row.factKey === `environment.flood.${suffix}` || row.factKey.startsWith(`environment.flood.${suffix}#`));
    if (rows.length === 0)
        return null;
    let answered = false;
    for (const row of rows) {
        const value = recordValue(row);
        if (value === null)
            continue;
        answered = true;
        if (inFloodZone(value))
            return true;
    }
    return answered ? false : null;
}
export function riskSummary(detail) {
    const flood = recordValue(factByKey(detail, 'environment.flood_zones'));
    const legacyQ100 = flood?.['in_Q100'];
    /*
     * Krok PRF5 bod 2: karta rizik v adminu uz nerika jen „zasahuje / nezasahuje",
     * ale STUPEN rizika — a bere ho z teze stupnice jako oba dokumenty reportu
     * (`floodZoneRiskLevel` v `@reos/duediligence`). Starsi klic
     * `environment.flood_zones` (kazety R3) zustava jako zaloha pro Q100.
     */
    const q100 = floodLayerReaches(detail, 'q100') ?? (typeof legacyQ100 === 'boolean' ? legacyQ100 : null);
    const q20 = floodLayerReaches(detail, 'q20');
    const q5 = floodLayerReaches(detail, 'q5');
    const q500 = floodLayerReaches(detail, 'q500');
    const activeZone = floodLayerReaches(detail, 'active_zone');
    const reach = q5 === true ? 'q5' : q20 === true ? 'q20' : q100 === true ? 'q100' : q500 === true ? 'q500' : null;
    return {
        inQ100: q100,
        floodRisk: floodZoneRiskLevel({
            answered: [q5, q20, q100, q500, activeZone].some((item) => item !== null),
            reach,
            inActiveZone: activeZone === true,
        }),
        nearestFloodZone: str(flood, 'nearest_flood_zone_m'),
        futureProjectCount: factsOf(detail, 'future').length,
        openGateCount: detail.fixture.gates.filter((g) => g.state !== 'CLOSED').length,
    };
}
/** Fakta pro zalozky, ktere vypisuji celou domenu. */
export function tabFacts(detail, tab) {
    switch (tab) {
        case 'geometrie':
            return detail.facts.filter((f) => f.domain === 'geometry' && !f.factKey.startsWith('access.'));
        case 'pristup':
            return detail.facts.filter((f) => f.factKey.startsWith('access.'));
        case 'site':
            return factsOf(detail, 'utilities');
        case 'uzemni-plan':
            return factsOf(detail, 'planning');
        case 'rizika':
            return [...factsOf(detail, 'environment'), ...factsOf(detail, 'future')];
        case 'analyzy':
            return factsOf(detail, 'market');
    }
}
/** Evidence serazena od nejnovejsiho stazeni (zalozka Dokumenty / zdroje reportu). */
export function evidenceRows(detail) {
    return [...detail.fixture.evidence].sort((a, b) => b.fetchedAt.localeCompare(a.fetchedAt));
}
/** Duvody, ktere by zuzily cenove pasmo (gate -> %), serazene od nejvetsiho vlivu. */
export function narrowingFactorsSorted(detail) {
    return [...detail.fixture.valuationBand.wouldNarrowIf].sort((a, b) => b.narrowsByPct - a.narrowsByPct);
}
/**
 * Trzni cenove body (Kc/m2) pro benchmark graf (BLOK 3 / B3-REDESIGN): cte
 * generickym zpusobem hodnoty faktu domeny `market` — jednotlive body
 * (`czk_m2`), historicky bod (`asking_czk_m2`) i vnorene polozky indexove
 * stranky (`items[].czk_m2`). Zadne cislo mimo fakta.
 */
export function marketBenchmarkRows(detail) {
    const rows = [];
    for (const fact of factsOf(detail, 'market')) {
        const record = recordValue(fact);
        if (record === null)
            continue;
        const czkM2 = num(record, 'czk_m2');
        if (czkM2 !== null) {
            rows.push({
                label: str(record, 'lokalita') ?? str(record, 'popis') ?? fact.factKey,
                czkPerM2: czkM2,
                historical: false,
                dated: fact.sourceDate,
            });
            continue;
        }
        const historicalCzkM2 = num(record, 'asking_czk_m2');
        if (historicalCzkM2 !== null) {
            rows.push({
                label: str(record, 'lokalita') ?? fact.factKey,
                czkPerM2: historicalCzkM2,
                historical: true,
                dated: str(record, 'claim_date') ?? fact.sourceDate,
            });
            continue;
        }
        const items = record['items'];
        if (Array.isArray(items)) {
            for (const item of items) {
                if (item === null || typeof item !== 'object')
                    continue;
                const itemRecord = item;
                const itemCzkM2 = itemRecord['czk_m2'];
                if (typeof itemCzkM2 !== 'number')
                    continue;
                const popis = itemRecord['popis'];
                rows.push({
                    label: typeof popis === 'string' ? popis : fact.factKey,
                    czkPerM2: itemCzkM2,
                    historical: false,
                    dated: fact.sourceDate,
                });
            }
        }
    }
    return rows.sort((a, b) => a.czkPerM2 - b.czkPerM2);
}
/** Prevede snake_case klic scenare na citelny titulek („scenar_A_prodej_vcelku" -> „A prodej vcelku"). */
function scenarioTitle(key) {
    const withoutPrefix = key.replace(/^scenar_/, '');
    return withoutPrefix.replace(/_/g, ' ');
}
/**
 * Indikativni scenare oceneni z faktu `valuation.indicative_scenarios`
 * (generickе `scenar_*` klice — pocet scenaru neni pevny, cte se z dat).
 */
export function valuationScenarios(detail) {
    const record = recordValue(factByKey(detail, 'valuation.indicative_scenarios'));
    if (record === null)
        return [];
    const out = [];
    for (const [key, value] of Object.entries(record)) {
        if (!key.startsWith('scenar_') || value === null || typeof value !== 'object' || Array.isArray(value))
            continue;
        const scenario = value;
        out.push({
            key,
            title: scenarioTitle(key),
            popis: str(scenario, 'popis'),
            indikace: str(scenario, 'indikace') ?? str(scenario, 'indikace_czk'),
        });
    }
    return out;
}
/** Otevrene (nezodpovezene) otazky serazene podle dopadu (CRITICAL prvni) — zalozka „Co nevime". */
export function openQuestionsSorted(detail) {
    const rank = { CRITICAL: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
    return detail.fixture.questions
        .filter((q) => q.status !== 'ANSWERED')
        .slice()
        .sort((a, b) => (rank[b.impact ?? ''] ?? 0) - (rank[a.impact ?? ''] ?? 0));
}
/** Checklist serazeny podle poradi ve fixture (uz je to priorita — brany prvni, pak otazky). */
export function checklistPriorityRows(detail) {
    return detail.fixture.checklist.map((item, i) => ({
        priority: i + 1,
        kind: item.kind,
        ref: item.ref,
        needs: item.needs,
        reviewRole: item.reviewRole,
        note: item.note,
    }));
}
//# sourceMappingURL=selectors.js.map